// Never infer a season from a timestamp or from "not the active event".
const FORM_KEY = /^form-([0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12})-(\d+)$/i
const VOTE_KEY = /^monterosa_sdk_element_.+_vote$/
const STAGING_HOST = 'cdn-us.monterosa.cloud'
const STAGING_FAN_PROJECT = '13f7743c-c166-473f-8132-529c06c5fab4'
const APPROVED_FORM_KEYS = [
  'form-d6ca7dea-ca5e-4175-9b8e-8a77eeb2ea1e-329',
  'form-61c0b4b9-35ec-4f74-a1bb-984ebea2074a-240',
  'form-ee2d97cb-6869-4a14-81de-e1e57b29f107-447',
  'form-a366fabc-1cba-401b-80dc-18ac7a0ee70d-449',
]

// Enabling this is a separate reviewed staging action, not an annual policy.
// Exact origin and exact vote-key ownership have NOT been verified by this patch.
export const STAGING_RESET_POLICY = Object.freeze({
  enabled: false,
  approvedOrigin: null,
  approvedVoteKeys: Object.freeze([]),
})

export const getDefaultVotingResetPolicy = (scope) =>
  scope?.season === 'AA27' && scope?.environment === 'staging' &&
  scope?.host === STAGING_HOST && scope?.projectId === STAGING_FAN_PROJECT
    ? STAGING_RESET_POLICY
    : { enabled: false }

export const parseVotingObject = (value) => {
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : null
  } catch {
    return null
  }
}

const storageKeys = (storage) =>
  Array.from({ length: storage.length }, (_, index) => storage.key(index))
    .filter(Boolean)

const validScope = (scope) => Boolean(scope &&
  ['season', 'host', 'environment', 'projectId'].every(
    (field) => typeof scope[field] === 'string' && scope[field].length > 0
  ))

export const getVotingIdentity = (meta) => {
  if (!validScope(meta) || !meta.eventId || meta.currentFormId == null ||
      String(meta.currentFormId) === '') return null
  return {
    schemaVersion: 1,
    season: meta.season,
    host: meta.host,
    environment: meta.environment,
    projectId: meta.projectId,
    eventId: meta.eventId,
    currentFormId: String(meta.currentFormId),
  }
}

export const withVotingIdentity = (value, meta) => {
  const identity = getVotingIdentity(meta)
  if (!identity) throw new Error('Voting identity is not ready')
  return { ...value, ...identity }
}

const sameScope = (record, scope) => validScope(scope) && record?.schemaVersion === 1 &&
  ['season', 'host', 'environment', 'projectId'].every(
    (field) => record[field] === scope[field]
  )

export const matchesVotingIdentity = (record, meta) => Boolean(
  getVotingIdentity(meta) && sameScope(record, meta) &&
  record.eventId === meta.eventId &&
  String(record.currentFormId) === String(meta.currentFormId)
)

export const getVotingMigrationKey = (scope) => {
  if (!validScope(scope)) throw new Error('Voting migration scope is not ready')
  // Shared across fan/judge projects: switching routes cannot authorize a reset.
  return `aa:voting:migration:${scope.season}:v1:${scope.host}:${scope.environment}`
}

const isCurrentSeasonTagged = (record, scope) => Boolean(record && (
  record.projectId === scope.projectId || record.season === scope.season
))

/**
 * Runs before configure()/getElements(). No reset when disabled. Destructive
 * staging mode requires exact allowlists, an explicit origin, and a Web Lock.
 * Never call SDK storageClear()/storagePersistent(false): both can clear origin.
 */
export const prepareVotingStorage = async ({
  storage,
  scope,
  policy = STAGING_RESET_POLICY,
  withLock,
  hasConfiguredSdk = false,
}) => {
  if (policy.enabled !== true) return { status: 'reset-disabled', didCleanup: false }
  if (!validScope(scope) || scope.season !== 'AA27' ||
      scope.environment !== 'staging' || scope.host !== STAGING_HOST ||
      scope.projectId !== STAGING_FAN_PROJECT) {
    throw new Error('Voting reset is not authorized for this environment/project')
  }
  if (!scope.origin || policy.approvedOrigin !== scope.origin) {
    throw new Error('Voting reset origin has not been approved')
  }
  if (!storage || !withLock) {
    throw new Error('Voting reset requires persistent storage and cross-tab locking')
  }
  const markerKey = getVotingMigrationKey(scope)
  return withLock(markerKey, async () => {
    const rawMarker = storage.getItem(markerKey)
    const marker = parseVotingObject(rawMarker)
    if (rawMarker !== null && (!marker || marker.schemaVersion !== 1 ||
        marker.season !== scope.season || marker.host !== scope.host ||
        marker.environment !== scope.environment ||
        !['pending', 'complete'].includes(marker.status))) {
      throw new Error('Voting migration marker is invalid; preserving storage')
    }
    if (marker?.status === 'complete') {
      return { status: 'already-complete', didCleanup: false }
    }
    if (hasConfiguredSdk) {
      throw new Error('Voting reset requires a full reload before SDK initialization')
    }
    const persistMarker = (value) => {
      const encoded = JSON.stringify(value)
      storage.setItem(markerKey, encoded)
      if (storage.getItem(markerKey) !== encoded) {
        throw new Error('Voting migration marker persistence failed')
      }
    }
    const keys = storageKeys(storage)
    const currentTagged = keys.filter((key) => FORM_KEY.test(key) ||
      key === 'formData' || key === 'loginFormState').some((key) =>
      isCurrentSeasonTagged(parseVotingObject(storage.getItem(key)), scope)
    )
    if (currentTagged && marker?.status === 'pending') {
      throw new Error('Incomplete migration has current-season state; preserve and review')
    }
    const markerBase = {
      schemaVersion: 1, season: scope.season,
      host: scope.host, environment: scope.environment,
    }
    if (currentTagged) {
      persistMarker({
        ...markerBase, status: 'complete', mode: 'preserved-current-season',
      })
      return { status: 'preserved-current-season', didCleanup: false }
    }
    if (!Array.isArray(policy.approvedVoteKeys) ||
        policy.approvedVoteKeys.some((key) => typeof key !== 'string' || !VOTE_KEY.test(key))) {
      throw new Error('Voting reset requires an exact vote-key allowlist')
    }
    const voteKeys = keys.filter((key) => VOTE_KEY.test(key))
    if (voteKeys.some((key) => !policy.approvedVoteKeys.includes(key))) {
      throw new Error('Unattributed SDK votes remain; voting reset is blocked')
    }
    const resetKeys = [...APPROVED_FORM_KEYS, ...policy.approvedVoteKeys]
    // Shared app prefill is removed only when its event/form is allowlisted.
    // Unknown formData/loginFormState and all authentication are preserved.
    for (const key of ['formData', 'loginFormState']) {
      const value = parseVotingObject(storage.getItem(key))
      if (value && APPROVED_FORM_KEYS.includes(
        `form-${value.eventId}-${value.currentFormId}`
      )) resetKeys.push(key)
    }
    // Reject changes to the retry boundary; do not silently broaden a partial reset.
    if (marker?.status === 'pending' &&
        (JSON.stringify(marker.voteKeys) !== JSON.stringify(policy.approvedVoteKeys))) {
      throw new Error('Pending migration allowlist changed; preserve and review')
    }
    persistMarker({
      ...markerBase, status: 'pending', voteKeys: policy.approvedVoteKeys,
    })
    let removed = 0
    for (const key of new Set(resetKeys)) {
      if (storage.getItem(key) !== null) {
        storage.removeItem(key)
        if (storage.getItem(key) !== null) throw new Error('Voting cache removal failed')
        removed += 1
      }
    }
    // A failed write leaves pending: callers must not hydrate SDK elements.
    persistMarker({
      ...markerBase, status: 'complete', mode: 'approved-staging-reset',
    })
    return { status: 'reset-complete', didCleanup: removed > 0 }
  })
}

// Adoption requires project-owned event metadata or the two fan-staging
// event/form pairs Brit verified in Monterosa. Unknown legacy records stay put.
// A missing projectId by itself is never evidence for deletion.
export const tagProjectFormRecords = ({ storage, scope, events }) => {
  if (!storage || !validScope(scope)) return
  // Listings can exclude past events; retain verified 447 -> 449 recovery.
  const verifiedStagingEvents = scope.season === 'AA27' &&
    scope.environment === 'staging' && scope.host === STAGING_HOST &&
    scope.projectId === STAGING_FAN_PROJECT
    ? APPROVED_FORM_KEYS.slice(2).map((key) => {
      const [, id, formId] = FORM_KEY.exec(key)
      return { id, context: { project: { id: scope.projectId } },
        fields: { formId: { data: [{ id: formId }] } } }
    }) : []
  for (const event of [...verifiedStagingEvents, ...events]) {
    if (event?.context?.project?.id !== scope.projectId) continue
    const formId = event.fields?.formId?.data?.[0]?.id
    const identity = getVotingIdentity({ ...scope, eventId: event.id, currentFormId: formId })
    if (!identity) continue
    const key = `form-${event.id}-${formId}`
    const record = parseVotingObject(storage.getItem(key))
    if (!record || (record.schemaVersion != null && record.schemaVersion !== 1)) continue
    if (['season', 'host', 'environment', 'projectId', 'eventId'].some(
      (field) => record[field] != null && record[field] !== identity[field]
    ) || (record.currentFormId != null &&
      String(record.currentFormId) !== identity.currentFormId)) continue
    const next = JSON.stringify({ ...record, ...identity })
    if (next !== storage.getItem(key)) storage.setItem(key, next)
  }
}

export const syncVotingFormData = ({ storage, meta }) => {
  const identity = getVotingIdentity(meta)
  if (!storage || !identity) return { isEventChanged: false, didCleanup: false }
  const stored = parseVotingObject(storage.getItem('formData'))
  const sameEventForm = stored?.eventId === identity.eventId &&
    String(stored?.currentFormId) === identity.currentFormId
  const conflicts = stored && ['projectId', 'host', 'environment', 'season'].some(
    (field) => stored[field] != null && stored[field] !== identity[field]
  )
  const preserve = sameEventForm && !conflicts
  const changed = Boolean(stored && (!sameEventForm || conflicts))
  const next = JSON.stringify({ ...(preserve ? stored : {}), ...identity })
  if (next !== storage.getItem('formData')) storage.setItem('formData', next)
  return {
    isEventChanged: Boolean(stored?.eventId && stored.eventId !== identity.eventId),
    didCleanup: changed, // App prefill only; no form records, SDK votes, or auth removed.
  }
}

export const getPreviousSubmissionCandidates = ({ storage, scope, activeEventId }) => {
  if (!storage || !validScope(scope)) return []
  return storageKeys(storage).flatMap((key) => {
    const match = FORM_KEY.exec(key)
    if (!match || match[1] === activeEventId) return []
    const record = parseVotingObject(storage.getItem(key))
    if (!sameScope(record, scope) || record.eventId !== match[1] ||
        String(record.currentFormId) !== match[2] || !record.lastVoteDate) return []
    const votedAt = Date.parse(record.lastVoteDate)
    if (!Number.isFinite(votedAt)) return []
    return [{ key, eventId: match[1], currentFormId: match[2], votedAt }]
  }).sort((a, b) => b.votedAt - a.votedAt)
}
